// keep in sync with App\Libraries\Facades\Event\EventTicketing\Enums\QuestionInputType.php (backend is canonical)
export enum QuestionInputType {
  SHORT_ANSWER = 'SHORT_ANSWER',
  PARAGRAPH = 'PARAGRAPH',
  PHONE = 'PHONE',
  NUMBER = 'NUMBER',
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
}

// keep in sync with App\Libraries\Facades\Event\EventTicketing\Enums\QuestionBelongsTo.php (backend is canonical)
export enum QuestionBelongsTo {
  PRODUCT = 'PRODUCT', // per ticket ("Each attendee")
  ORDER = 'ORDER', // per order ("Buyer")
}

// keep in sync with App\Libraries\Facades\Event\EventTicketing\Enums\TaxMode.php (backend is canonical)
export enum TaxMode {
  EXCLUSIVE = 'EXCLUSIVE', // typed price is pre-tax; tax added on top (its own line)
  INCLUSIVE = 'INCLUSIVE', // typed price already includes tax; shown as a "tax included" note
}

export interface TicketingQuestion {
  text: string;
  type: QuestionInputType;
  required: boolean;
  options: string[]; // order = array position; non-empty only for SINGLE_SELECT / MULTIPLE_CHOICE
}

export interface TicketingConfig {
  isSelected: boolean;
  isRsvp: boolean;
  ticketName: string;
  price: number;
  totalTickets: number | null; // null = unlimited
  maxPerOrder: number | null; // null = field left empty mid-edit; defaults to DEFAULT_MAX_PER_ORDER on blur
  passFeesToBuyer: boolean;
  taxLabel: string; // buyer-visible tax name (e.g. "VAT"); empty ⇒ no tax
  taxRatePercent: number; // 0 ⇒ no tax
  taxMode: TaxMode;
  buttonText: string; // Tickets-tile CTA label (frontend session only — never serialized; consolidated into register_btn_text on save)
  rsvpButtonText: string; // RSVP-tile CTA label, kept separate from buttonText so each tile remembers its own choice
  whoResponds: QuestionBelongsTo;
  questionSectionTitle: string;
  questionSectionDescription: string;
  questions: TicketingQuestion[]; // order = array position
}

// Mirrors TicketingConfigVO::DEFAULT_MAX_PER_ORDER, the ceiling the save path stores when the posted value is
// missing or non-numeric. The two must move together, or clearing the field saves a different ceiling than the
// one the blur handler fills back in.
export const DEFAULT_MAX_PER_ORDER = 5;

export const getDefaultTicketingConfig = (): TicketingConfig => ({
  isSelected: false,
  isRsvp: false,
  ticketName: '',
  price: 0,
  totalTickets: null,
  maxPerOrder: DEFAULT_MAX_PER_ORDER,
  passFeesToBuyer: false,
  taxLabel: '',
  taxRatePercent: 0,
  taxMode: TaxMode.EXCLUSIVE,
  buttonText: '', // Tickets-tile CTA label; empty shows the mode's first preset and is saved via register_btn_text
  rsvpButtonText: '', // RSVP-tile CTA label; empty shows the RSVP default ("Register")
  whoResponds: QuestionBelongsTo.ORDER, // Figma default = "Buyer"
  questionSectionTitle: '',
  questionSectionDescription: '',
  questions: [],
});

export interface TicketingFeeParams {
  serviceFeePercent: number; // threshold-aware effective platform rate when editing a provisioned event; else the live below-threshold rate
  serviceFeeFixed: number;
  serviceFeeCap: number | null; // per-ticket platform-fee cap; null = uncapped (PAYG / past the volume threshold)
  processingFeePercent: number;
  processingFeeFixed: number;
}

// Client-side validation bounds — keep in sync with App\Models\Event\VO\TicketingConfigVO (backend is canonical).
// Accepts an in-progress USD amount as typed: optional digits, optional single decimal point, up to
// two fraction digits (e.g. '', '5', '0.', '12.99'). Rejects letters and 3+ decimal places per keystroke.
export const PRICE_INPUT_PATTERN = /^\d*\.?\d{0,2}$/;
// Accepts an in-progress ticket count as typed: digits only, empty allowed so the field can be cleared (e.g. '',
// '5', '250'). A count is positive and whole, so this rejects the sign and decimal characters `<input
// type="number">` would otherwise accept alongside letters and 'e' exponents. Enforcing it needs the field to be
// a text input: on a number input the browser reports an unparseable value as '', which this pattern accepts,
// so the guard would pass and the count would silently reset while the typed text stayed on screen.
export const POSITIVE_WHOLE_NUMBER_INPUT_PATTERN = /^\d*$/;
export const MIN_PRICE = 10; // Product floor for a paid ticket (also clears Stripe's USD $0.50 minimum); below it only free (0) is allowed. Mirrors TicketingConfigVO::MIN_PRICE.
export const MAX_PRICE = 1_000_000;
export const MAX_PER_ORDER = 100;
export const MAX_TOTAL_TICKETS = 10_000; // Mirrors TicketingConfigVO::MAX_TOTAL_TICKETS, which the save path clamps to silently.
export const MAX_BUTTON_TEXT_LENGTH = 50;
export const MAX_SECTION_TITLE_LENGTH = 100;
export const MAX_SECTION_DESCRIPTION_LENGTH = 500;

/**
 * The mode-aware default for the custom-form title: "Register" for RSVP, "Buy tickets" for paid
 * ticketing, "Get tickets" for free ticketing. Used as the title field's placeholder and saved
 * verbatim when the organiser leaves the title empty, so the field, the save payload, and the
 * checkout preview all agree on the heading.
 */
export const getDefaultTicketingFormTitle = (config: TicketingConfig): string => {
  if (config.isRsvp) {
    return window.i18next.t('pmwjs_register');
  }
  if (config.price > 0) {
    return window.i18next.t('pmwjs_event_ticketing_button_buy_tickets');
  }
  return window.i18next.t('pmwjs_event_ticketing_button_get_tickets');
};

export const DEFAULT_OPTION_COUNT = 2;
export const MIN_CHOICE_OPTIONS = 2;

export interface QuestionTypeDescriptor {
  icon: string;
  labelKey: string;
}

export const QUESTION_TYPE_DESCRIPTORS: Record<QuestionInputType, QuestionTypeDescriptor> = {
  [QuestionInputType.SHORT_ANSWER]: {icon: 'icon-text', labelKey: 'pmwjs_event_ticketing_type_short_answer'},
  [QuestionInputType.PARAGRAPH]: {icon: 'icon-align-left', labelKey: 'pmwjs_event_ticketing_type_paragraph'},
  [QuestionInputType.PHONE]: {icon: 'icon-phone', labelKey: 'pmwjs_event_ticketing_type_phone'},
  [QuestionInputType.NUMBER]: {icon: 'icon-number', labelKey: 'pmwjs_event_ticketing_type_number'},
  [QuestionInputType.SINGLE_SELECT]: {icon: 'icon-radio-button-checked', labelKey: 'pmwjs_event_ticketing_type_single_select'},
  [QuestionInputType.MULTIPLE_CHOICE]: {icon: 'icon-checkbox-checked', labelKey: 'pmwjs_event_ticketing_type_multiple_choice'},
};

export interface TicketingWhoRespondsOption {
  value: QuestionBelongsTo;
  label: string;
  subText: string;
}

/**
 * An RSVP event has no buyer, so its order-scoped option is worded "Registrant". The per-ticket option
 * keeps the same wording in both modes — an attendee is an attendee whether or not money changed hands.
 */
export const getTicketingWhoRespondsOptions = (isRsvp: boolean): TicketingWhoRespondsOption[] => {
  return [
    {
      value: QuestionBelongsTo.ORDER,
      label: isRsvp ? window.i18next.t('pmwjs_registrant') : window.i18next.t('pmwjs_event_ticketing_who_buyer'),
      subText: isRsvp ? window.i18next.t('pmwjs_collect_details_from_registrant_only') : window.i18next.t('pmwjs_event_ticketing_who_buyer_sub'),
    },
    {
      value: QuestionBelongsTo.PRODUCT,
      label: window.i18next.t('pmwjs_event_ticketing_who_attendee'),
      subText: window.i18next.t('pmwjs_event_ticketing_who_attendee_sub'),
    },
  ];
};
